import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Tooltip,
} from '@chakra-ui/react'
import type { Location } from 'history'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import useFetch, { CachePolicies } from 'use-http'
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import { FileDetailHeaderToolbar } from '../../components/MediaLibrary'
import AccessDeniedView from '../../components/MediaLibrary/View/AccessDeniedView'
import NotFoundView from '../../components/MediaLibrary/View/NotFoundView'
import { useDetailLinkBase } from '../../hooks/useDetailLinkBase'
import type {
  MediaLibrarySearchFileResponse,
  ViewableParentRequest,
  ViewableParentSuccessResponse,
  MLFileDetailRouteParams,
} from '../../interfaces'
import type { FileDetailsResponse } from '../../interfaces/FileDetailsResponse'
import { FileDetailPage } from '../../pages/MediaLibrary'
import { MLContext, UserContext } from '../../providers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import { filesPerPage } from '../../../utils/mediaLibrary'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { useDebounce } from '../../hooks/useDebounce'

interface Props {
  isOpen: boolean
  onClose(): void
}

const FileDetailModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement | null => {
  const history = useHistory()
  const location = useLocation()

  const [
    {
      fileList,
      recentFileFolderList,
      mediaCollectionRoute,
      currentMediaCollection,
      filePage,
      loadingFiles,
      selectedFiles,
      detailPageVersions,
    },
    dispatch,
  ] = useContext(MLContext)

  const { isPublicLink, urlPrefix, clientInfo, assetType } = useContext(
    PublicLinksInfoContext,
  )
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber ?? clientInfo?.clientNumber

  const {
    mediaCollectionId: pathMediaCollectionId,
    fileId: pathFileId,
    versionId: pathVersionId,
  } = useParams<MLFileDetailRouteParams>()

  const { background } = useDetailLinkBase()
  // to make sure the modal is focused on open for accessibility and focus doesn't go to elements that show tooltip
  const initialFocusRef = useRef<HTMLDivElement | null>(null)

  const {
    error: fileDetailsError,
    get,
    loading,
  } = useFetch<FileDetailsResponse>({ cachePolicy: CachePolicies.NO_CACHE })

  const { post, error: viewableParentError } =
    useFetch<ViewableParentSuccessResponse>({
      cachePolicy: CachePolicies.NO_CACHE,
    })

  const [fileDetail, setFileDetail] = useState<FileDetailsResponse | undefined>(
    selectedFiles[0] as unknown as FileDetailsResponse,
  )

  const url = useMemo(
    () =>
      pathVersionId
        ? `${urlPrefix}api/v2/files/${pathFileId}/versions/${pathVersionId}`
        : `${urlPrefix}api/v2/files/${pathFileId}/details`,
    [pathFileId, pathVersionId, urlPrefix],
  )

  const navigableRecentFiles = useMemo(
    () => recentFileFolderList.filter(item => item.type === 'file'),
    [recentFileFolderList],
  ) as MediaLibrarySearchFileResponse[]

  const navigationList = useMemo(
    () => [...navigableRecentFiles, ...fileList],
    [fileList, navigableRecentFiles],
  )

  const getFileDetails = useCallback(() => get(url), [url])

  const getAndSetFileDetails = useCallback(async () => {
    const details = await getFileDetails()
    if (!fileDetailsError && !viewableParentError) {
      setFileDetail(details)
    }
  }, [getFileDetails])

  const debouncedPathFileId = useDebounce(pathFileId, 500)

  const getViewableParent = useCallback(() => {
    const body: ViewableParentRequest = { assetId: pathFileId }
    if (pathMediaCollectionId) {
      body.mediaCollectionId = pathMediaCollectionId
    }
    return post(
      `${urlPrefix}api/clients/${clientNumber}/access-control/viewable-parent`,
      body,
    )
  }, [debouncedPathFileId, pathMediaCollectionId, clientNumber, urlPrefix])

  useEffect(() => {
    if (
      (isPublicLink && assetType !== 'file') ||
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      void getViewableParent()
    }
  }, [getViewableParent, user, isPublicLink, assetType])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
        return
      }

      e.preventDefault()

      const idx = navigationList.findIndex(x => x.fileId === pathFileId)
      if (idx === -1) {
        return
      }

      const next =
        e.key === 'ArrowRight'
          ? navigationList[idx + 1]
          : navigationList[idx - 1]
      if (!next) {
        return
      }

      const nextPathname =
        mediaCollectionRoute && currentMediaCollection
          ? `/media-collections/${currentMediaCollection}/detail/${next.fileId}`
          : location.pathname.startsWith('/search')
          ? `/search/detail/${next.fileId}`
          : `/media-library/detail/${next.fileId}`

      history.replace({
        pathname: nextPathname,
        search: location.search,
        state: { background },
      })
    }

    window.addEventListener('keydown', onKeyDown)
    return () => void window.removeEventListener('keydown', onKeyDown)
  }, [
    pathFileId,
    fileList,
    mediaCollectionRoute,
    currentMediaCollection,
    filePage,
    loadingFiles,
  ])

  useEffect(() => {
    const idx = fileList.findIndex(x => x.fileId === pathFileId)
    if (idx === -1) {
      return
    }

    if (
      !loadingFiles &&
      fileList.length > 0 &&
      fileList.length % filesPerPage === 0 &&
      fileList.length / filesPerPage === filePage + 1 &&
      idx >= fileList.length - 1
    ) {
      dispatch({ type: MediaLibraryActions.LOAD_MORE_FILES })
    }
  }, [pathFileId, fileList, filePage, loadingFiles])

  useEffect(() => {
    const fileIdx = navigationList.findIndex(x => x.fileId === pathFileId)
    if (fileIdx !== -1 && !pathVersionId) {
      setFileDetail(navigationList[fileIdx] as unknown as FileDetailsResponse)
      return
    }
    const versionIdx = detailPageVersions.findIndex(
      x => x.versionInfo.id === pathVersionId && x.type !== 'file',
    )
    if (versionIdx !== -1) {
      setFileDetail(
        detailPageVersions[versionIdx] as unknown as FileDetailsResponse,
      )
      return
    }
    void getAndSetFileDetails()
  }, [navigationList, pathFileId, pathVersionId, getAndSetFileDetails])

  const unionisedError = viewableParentError ?? fileDetailsError
  const canView = isPublicLink || user?.pricingPlan.featuresList.includes(FeaturesEnum.MEDIA_LIBRARY_STORAGE)

  const handleClose = (): void => {
    dispatch({
      type: MediaLibraryActions.SET_DISABLE_DETAIL_NAVIGATION,
      payload: false,
    })
    onClose()
  }

  if (!fileDetail && !unionisedError) {
    return null
  }

  return (
    <Modal
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent
        h="100vh"
        maxH="100vh"
        w="100vw"
        m={0}
        borderRadius="0"
        display="flex"
        flexDir="column"
      >
        <Box ref={initialFocusRef} tabIndex={-1} aria-hidden="true" />
        <Flex
          as="header"
          flexShrink={0}
          position="sticky"
          top={0}
          zIndex={2}
          bg="white"
          borderBottomWidth="1px"
          borderColor="gray.200"
          px="24px"
          py="12px"
          align="center"
          justify="space-between"
        >
          <Box flex="1" minW={0}>
            {fileDetail && !unionisedError && (
              <FileDetailHeaderToolbar
                fileDetails={fileDetail}
                getFileDetails={getFileDetails}
                setFileDetails={setFileDetail}
                loading={loading}
                handleClose={handleClose}
              />
            )}
          </Box>

          <Tooltip label="Close">
            <ModalCloseButton
              minHeight="unset"
              padding="0px"
              height="21px"
              position="static"
              ml="8px"
              color="#1a202c"
              _hover={{
                bg: 'none',
              }}
              _active={{
                bg: 'none',
              }}
              _focus={{
                boxShadow: 'none',
              }}
            />
          </Tooltip>
        </Flex>

        <ModalBody
          p={0}
          display="flex"
          flex="1 1 0%"
          minH={0}
          overflow="hidden"
        >
          {!canView ? (
            <Flex flex="1" align="center" justify="center">
              <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
            </Flex>
          ) : unionisedError ? (
            unionisedError.name === '403' ? (
              <Flex flex="1" align="center" justify="center">
                <AccessDeniedView />
              </Flex>
            ) : unionisedError.name === '404' ? (
              <Flex flex="1" align="center" justify="center">
                <NotFoundView />
              </Flex>
            ) : null
          ) : (
            <Flex w="100%" h="100%" minH={0}>
              <Box flex="1 1 auto" minW={0} minH={0}>
                <FileDetailPage
                  fileDetails={fileDetail}
                  setFileDetails={setFileDetail}
                  loading={loading}
                />
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default FileDetailModal
